import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative">
      <img
        src={images[currentIndex]}
        alt={`${title} - view ${currentIndex + 1}`}
        className="w-full h-64 object-cover rounded-xl"
        onError={(e) => {
          e.target.src = require("../../assets/images/ppe/placeholder.jpg");
          e.target.onerror = null;
        }}
      />

      {/* Navigation Arrows */}
      <button
        onClick={prevImage}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
      >
        ←
      </button>
      <button
        onClick={nextImage}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
      >
        →
      </button>

      {/* Image Indicators */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`w-2 h-2 rounded-full ${idx === currentIndex ? 'bg-white' : 'bg-white/50'
              }`}
          />
        ))}
      </div>
    </div>
  );
};

const ProtectiveEquipment = () => {
  const equipment = [
    {
      title: "Head Protection",
      description: "A safety helmet is a protective headgear designed to safeguard workers from head injuries caused by falling objects, impacts, or electrical hazards. It is widely used in industries such as construction, manufacturing, welding, and fabrication.",
      color: "text-yellow-600",
      icon: "👷‍♂️",
      images: [
        require("../../assets/images/ppe/safety-helmet.jpg"),
        require("../../assets/images/ppe/safety-helmet-2.jpg"),
        require("../../assets/images/ppe/safety-helmet-3.jpg"),
        require("../../assets/images/ppe/safety-helmet-4.jpg")
      ],
      features: [
        "Impact resistance",
        "Electrical insulation",
        "Adjustable fitting",
        "Weather resistant",
        "High visibility options"
      ],
      applications: [
        "Construction sites",
        "Manufacturing plants",
        "Welding operations",
        "Fabrication workshops",
        "Mining operations"
      ]
    },
    {
      title: "Foot Protection",
      description: "Safety shoes are protective footwear designed to safeguard workers from workplace hazards such as heavy objects, sharp materials, electrical risks, chemicals, and extreme temperatures. They are essential for industries like construction, welding, fabrication, manufacturing, and industrial work.",
      color: "text-brown-600",
      icon: "👞",
      images: [
        require("../../assets/images/ppe/safety-shoes.jpg"),
        require("../../assets/images/ppe/safety-shoes-2.jpg"),
        require("../../assets/images/ppe/safety-shoes-3.jpg"),
        require("../../assets/images/ppe/safety-shoes-4.jpg")
      ],
      features: [
        "Steel toe protection",
        "Anti-slip soles",
        "Electrical hazard protection",
        "Chemical resistance",
        "Comfort padding"
      ],
      applications: [
        "Construction work",
        "Welding operations",
        "Factory floors",
        "Chemical plants",
        "Heavy industry"
      ]
    },
    {
      title: "Eye Protection",
      description: "Safety goggles are protective eyewear designed to shield the eyes from hazards such as dust, chemicals, sparks, debris, and harmful radiation. They are widely used in industries like welding, fabrication, construction, manufacturing, and laboratories.",
      color: "text-blue-600",
      icon: "🥽",
      images: [
        require("../../assets/images/ppe/safety-goggles.jpg"),
        require("../../assets/images/ppe/safety-goggles-2.jpg"),
        require("../../assets/images/ppe/safety-goggles-3.jpg"),
        require("../../assets/images/ppe/safety-goggles-4.jpg")
      ],
      features: [
        "Impact resistance",
        "UV protection",
        "Anti-fog coating",
        "Chemical splash protection",
        "Adjustable fit"
      ],
      applications: [
        "Welding work",
        "Laboratory operations",
        "Construction sites",
        "Chemical handling",
        "Manufacturing processes"
      ]
    },
    {
      title: "Safety Harness",
      description: "A safety harness is a protective device designed to prevent falls from heights by securely attaching the wearer to an anchor point. It is essential for industries such as construction, fabrication, maintenance, scaffolding, and other high-altitude work environments.",
      color: "text-red-600",
      icon: "🔒",
      images: [
        require("../../assets/images/ppe/safety-harness.jpg"),
        require("../../assets/images/ppe/safety-harness-2.jpg"),
        require("../../assets/images/ppe/safety-harness-3.jpg"),
        require("../../assets/images/ppe/safety-harness-4.jpg")
      ],
      features: [
        "Full body protection",
        "Multiple attachment points",
        "Adjustable straps",
        "Quick-release buckles",
        "High-strength webbing"
      ],
      applications: [
        "High-rise construction",
        "Tower maintenance",
        "Scaffolding work",
        "Roof operations",
        "Industrial climbing"
      ]
    },
    {
      title: "Hand Protection",
      description: "Safety gloves are protective handwear designed to safeguard workers from various hazards such as cuts, burns, chemicals, electrical shocks, and extreme temperatures. They are essential for industries like welding, fabrication, construction, manufacturing, and chemical handling.",
      color: "text-green-600",
      icon: "🧤",
      images: [
        require("../../assets/images/ppe/safety-gloves.jpg"),
        require("../../assets/images/ppe/safety-gloves-2.jpg"),
        require("../../assets/images/ppe/safety-gloves-3.jpg"),
        require("../../assets/images/ppe/safety-gloves-4.jpg")
      ],
      features: [
        "Cut resistance",
        "Heat protection",
        "Chemical resistance",
        "Electrical insulation",
        "Grip enhancement"
      ],
      applications: [
        "Welding operations",
        "Chemical handling",
        "Construction work",
        "Manufacturing tasks",
        "Electrical work"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-[160px]">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center bg-gradient-to-b from-gray-900 via-gray-800 to-[#333333] overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]" />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/20 via-transparent to-gray-800/20" />

          {/* Animated Particles */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="absolute inset-0"
          >
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-gray-400 rounded-full"
                initial={{
                  x: Math.random() * 100 + "%",
                  y: Math.random() * 100 + "%",
                  opacity: Math.random() * 0.3 + 0.1
                }}
                animate={{
                  y: [Math.random() * 100 + "%", Math.random() * 100 + "%"],
                  opacity: [Math.random() * 0.3 + 0.1, Math.random() * 0.3 + 0.1]
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            ))}
          </motion.div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="inline-block mb-6 relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 text-transparent bg-clip-text text-lg font-semibold tracking-wider uppercase">
                  Safety First
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-5xl md:text-6xl font-bold text-white mb-6"
              >
                Personal{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-white">
                    Protective
                  </span>
                  {/* <motion.span
                    className="absolute bottom-0 left-0 w-full h-2 bg-gray-400 opacity-50"
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 1, delay: 1 }}
                  /> */}
                </span>{" "}
                Equipment
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl text-white max-w-3xl mx-auto"
              >
                Essential safety gear for workplace protection and hazard prevention,
                designed to keep you safe in any industrial environment.
              </motion.p>

              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="mt-16 h-1 max-w-xl mx-auto bg-gradient-to-r from-transparent via-gray-400 to-transparent"
              />

              {/* <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-wrap justify-center gap-4"
              >
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Get Started
                </Link>
                <Link
                  to="/products"
                  className="px-8 py-4 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  View All Products
                </Link>
              </motion.div> */}
            </div>

            {/* Safety Stats */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
            >
              {[
                { number: "1000+", label: "Products" },
                { number: "500+", label: "Happy Clients" },
                { number: "15+", label: "Years Experience" },
                { number: "24/7", label: "Support" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <h3 className="text-3xl font-bold text-white mb-2">{stat.number}</h3>
                  <p className="text-gray-300">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div> */}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {equipment.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="mb-6">
                  <ImageCarousel images={item.images} title={item.title} />
                  <div className="flex items-center mt-6">
                    {/* <span className="text-4xl mr-4">{item.icon}</span> */}
                    <div>
                      <h2 className="text-3xl font-bold text-black mb-2">
                        {item.title}
                      </h2>
                      <div className="w-32 h-1 bg-gray-200"></div>
                    </div>
                  </div>
                </div>

                <p className="text-black text-lg mb-6">
                  {item.description}
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-black mb-3">
                      Key Features
                    </h3>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {item.features.map((feature, featureIdx) => (
                        <li key={featureIdx}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-black mb-3">
                      Applications
                    </h3>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {item.applications.map((application, appIdx) => (
                        <li key={appIdx}>{application}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Notice Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-3xl font-bold text-black mb-6">
              Safety First
            </h2>
            <div className="space-y-4 text-black">
              <p>
                Personal protective equipment is your last line of defense against workplace hazards.
                Always ensure proper fit, maintenance, and usage according to manufacturer guidelines.
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Inspect PPE before each use</li>
                <li>Replace damaged or worn equipment immediately</li>
                <li>Ensure proper fit and adjustment</li>
                <li>Use appropriate PPE for the specific task</li>
                <li>Maintain clean and proper storage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
              Need Help Selecting PPE?
            </h2>
            <p className="text-black mb-8">
              Our safety experts are here to help you choose the right protective equipment for your specific workplace requirements
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Contact Our Experts
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProtectiveEquipment;